import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarDays,
  Check,
  Copy,
  ExternalLink,
  Link2,
  MoreHorizontal,
  RefreshCw,
  Settings,
  Share2,
  X,
} from 'lucide-react';
import { useWorkspace } from '../../../WorkspaceContext';
import { getBookingLink } from '../../../components/BookingLinkActions';

/**
 * Single overflow control for Front Desk. Actions live in a bottom sheet on
 * mobile and a centered modal on desktop — keeps the page header quiet.
 */
const FrontDeskMenu = ({ refreshing = false, onRefresh }) => {
  const { activeLocation } = useWorkspace();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const { path, url } = getBookingLink(activeLocation);
  const bookingOn = activeLocation?.bookingEnabled !== false;

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open]);

  const close = () => setOpen(false);

  const copyLink = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // no-op — clipboard can fail in insecure contexts
    }
  };

  const shareLink = async () => {
    if (!url) return;
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title: activeLocation?.name || 'Book with us',
          text: `Book online at ${activeLocation?.name || 'our salon'}`,
          url,
        });
        close();
        return;
      } catch (error) {
        if (error?.name === 'AbortError') return;
      }
    }
    await copyLink();
  };

  const actionClass =
    'flex w-full items-center gap-3 px-4 py-3.5 text-left text-sm text-ink-900 transition-colors hover:bg-ink-900/5';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Front desk actions"
        className="inline-flex h-10 flex-none items-center gap-2 px-3 text-sm font-medium text-ink-900 ring-1 ring-ink-900/15 transition-colors hover:bg-white"
      >
        <MoreHorizontal className="h-5 w-5" />
        <span>Quick actions</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-ink-950/50 p-0 sm:items-center sm:p-4"
          onClick={close}
          role="presentation"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="front-desk-menu-title"
            onClick={(event) => event.stopPropagation()}
            className="w-full max-w-md bg-sand-50 shadow-[0_30px_60px_-30px_rgba(6,18,15,0.55)] ring-1 ring-ink-900/10 sm:max-h-[90vh] sm:overflow-y-auto"
          >
            <div className="flex items-center justify-between border-b border-ink-900/10 px-5 py-4">
              <div className="min-w-0">
                <p
                  id="front-desk-menu-title"
                  className="font-display text-xl font-light text-ink-900"
                >
                  Quick actions
                </p>
                {activeLocation?.name && (
                  <p className="mt-0.5 truncate text-xs text-ink-600/65">{activeLocation.name}</p>
                )}
              </div>
              <button
                type="button"
                onClick={close}
                aria-label="Close"
                className="flex h-9 w-9 items-center justify-center text-ink-700 transition-colors hover:bg-ink-900/5"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-5 py-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-600/55">
                Booking link
              </p>
              {path && url ? (
                <div className="mt-2 bg-white px-3 py-3 ring-1 ring-ink-900/10">
                  <div className="flex items-start gap-2">
                    <Link2 className="mt-0.5 h-4 w-4 flex-none text-saffron-600" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-ink-900">
                        /book/{activeLocation.slug}
                      </p>
                      <p className="mt-0.5 truncate text-xs text-ink-600/60">{url}</p>
                      {!bookingOn && (
                        <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-red-700">
                          Booking is off
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="mt-2 text-sm text-ink-700/75">
                  No booking link yet. Set one up in Manage.
                </p>
              )}
            </div>

            <ul className="border-t border-ink-900/10">
              {url && (
                <>
                  <li>
                    <button type="button" onClick={shareLink} className={actionClass} data-tour="front-desk-share-booking">
                      <Share2 className="h-4 w-4 text-ink-600/70" />
                      Share booking link
                    </button>
                  </li>
                  <li>
                    <button type="button" onClick={copyLink} className={actionClass}>
                      {copied ? (
                        <Check className="h-4 w-4 text-saffron-600" />
                      ) : (
                        <Copy className="h-4 w-4 text-ink-600/70" />
                      )}
                      {copied ? 'Copied' : 'Copy booking link'}
                    </button>
                  </li>
                  <li>
                    <a
                      href={path}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={close}
                      className={actionClass}
                    >
                      <ExternalLink className="h-4 w-4 text-ink-600/70" />
                      Preview booking page
                    </a>
                  </li>
                </>
              )}
              <li>
                <Link
                  to="/app/manage/booking-link"
                  onClick={close}
                  className={actionClass}
                >
                  <Settings className="h-4 w-4 text-ink-600/70" />
                  {path ? 'Manage booking link' : 'Set up booking link'}
                </Link>
              </li>
            </ul>

            <ul className="border-t border-ink-900/10">
              <li>
                <button
                  type="button"
                  disabled={refreshing}
                  onClick={() => {
                    onRefresh?.();
                    close();
                  }}
                  className={`${actionClass} disabled:opacity-50`}
                >
                  <RefreshCw
                    className={`h-4 w-4 text-ink-600/70 ${refreshing ? 'animate-spin' : ''}`}
                  />
                  Refresh appointments
                </button>
              </li>
              <li>
                <Link to="/app/calendar" onClick={close} className={actionClass}>
                  <CalendarDays className="h-4 w-4 text-ink-600/70" />
                  Open calendar
                </Link>
              </li>
            </ul>

            {/* Safe-area spacer for home indicator on phones */}
            <div className="h-2 sm:hidden" />
          </div>
        </div>
      )}
    </>
  );
};

export default FrontDeskMenu;
